<script>
export default {
  inheritAttrs: false,
}
</script>

<script setup>
import { format } from 'quasar'
import FormFieldGenerator from 'src/generators/formFieldGenerator.vue'
import draggable from 'vuedraggable'

const dragOptions = computed(() => {
  return {
    animation: 200,
    disabled: false,
    ghostClass: 'ghost',
  }
})
const attrs = useAttrs()
const { capitalize } = format
const { t } = useI18n({ useScope: 'global' })
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      return {}
    },
  },
  formErrors: {
    type: Object,
    default: () => {
      return {}
    },
  },
  schema: {
    type: Object,
    default: () => {
      return {}
    },
  },
  tabError: {
    type: undefined,
    required: false,
    default: undefined,
  },
  itemsService: {
    type: String,
    default: '',
  },
  nested: {
    type: Boolean,
    default: true,
  },
  dense: {
    type: Boolean,
    default: false,
  },
  showHeader: {
    type: Boolean,
    default: false,
  },
  fields: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  currentTabIndex: {
    type: Number,
    required: false,
    default: 0,
  },
  optionsConfig: {
    type: undefined,
    required: false,
    default: undefined,
  },
  requireNoneOrAll: {
    type: Boolean,
    required: false,
    default: false,
  },
  parentHasError: {
    type: Boolean,
    required: false,
    default: false,
  },
  readOnlyOn: {
    type: Object,
    required: false,
    default: () => {
      return {}
    },
  },
})

const emit = defineEmits(['update:modelValue', 'update:tabError', 'formError', 'formValid', 'removeAction', 'checkAll', 'endDrag', 'startDrag'])
const writableModel = ref(props.modelValue)
watch(
  () => writableModel,
  (newValue) => {
    emit('update:modelValue', newValue)
  },
)

watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue) {
      writableModel.value = props.modelValue
    }
  },
)

watchEffect(() => {
  for (const key of Object.keys(props.schema)) {
    if (props.formErrors[key]) {
      if (props.fields.length === 0 || props.fields.includes(key)) {
        emit('formError', key)
      }
    }
  }
})

const translationString = computed(() => {
  let prefix = process.env.FRONTEND_TYPE + '.' + capitalize(props.itemsService)
  if (props.itemsService === 'users') {
    prefix = 'Shared.Users'
  }
  return prefix
})

const val = ref(0)

const checkAll = ref({})
const checkAllType = ref('all')

for (const key of Object.keys(props.schema)) {
  if (typeof props.schema[key].checkAll !== 'undefined') {
    checkAll.value[key] = props.schema[key].checkAll
    watch(
      () => props.schema[key].checkAll,
      (newValue, oldValue) => {
        if (newValue !== checkAll.value[key]) {
          checkAllType.value = 'box-only'
          checkAll.value[key] = newValue
        }
      },
    )
  }
}

let previousValue = structuredClone(toRaw(checkAll.value))
watch(
  () => checkAll.value,
  (newValue) => {
    if (checkAllType.value === 'box-only') {
      checkAllType.value = 'all'
      previousValue = structuredClone(toRaw(newValue))
      return
    }

    // Find out the value that changed
    const changedKey = Object.keys(newValue).find((key) => {
      return newValue[key] !== previousValue[key]
    })

    if (changedKey) {
      emit('checkAll', changedKey, newValue[changedKey])
    }

    previousValue = structuredClone(toRaw(newValue))
  },
  {
    deep: true,
  },
)

const invalidFields = ref([])

const receivedFieldError = (itemKey, itemIndex = null, itemKeyName = null) => {
  invalidFields.value.push(itemKey)
  if (!isNaN(itemIndex) && itemKeyName) {
    fieldHasError.value[itemKey] = {
      index: itemIndex,
      keyName: itemKeyName,
    }
  }
  emit('formError', itemKey)
}
const receivedFieldNoError = (itemKey, itemIndex = null, itemKeyName = null) => {
  if (!isNaN(itemIndex) && itemKeyName && fieldHasError.value[itemKey]) {
    delete fieldHasError.value[itemKey][itemIndex]
    delete fieldHasError.value[itemKey][itemKeyName]
  }
}

const receivedFormError = (itemKey) => {
  invalidFields.value.push(itemKey)
  emit('formError', itemKey)
}

const addObjectItemToArray = (key) => {
  let keyObj = {}
  for (let schemaKey of Object.keys(props.schema[key].value)) {
    if (props.schema[key].value[schemaKey].type === 'object' || props.schema[key].value[schemaKey].type === 'custom-object') {
      keyObj[schemaKey] = {}
      for (let childSchemaKey of Object.keys(props.schema[key].value[schemaKey].value)) {
        keyObj[schemaKey][childSchemaKey] = props.schema[key].value[schemaKey].value[childSchemaKey].value
      }
    } else {
      keyObj[schemaKey] = props.schema[key].value[schemaKey].value
    }
  }
  if (!writableModel.value[key]) {
    writableModel.value[key] = []
  }
  writableModel.value[key].push(keyObj)
}

const addItemToArray = (key) => {
  writableModel.value[key].push('')
}

const removeItemFromArray = (key, index) => {
  emit('removeAction', {
    value: writableModel.value,
    key,
    index,
  })
}

const objectMinusHiddenFields = (object) => {
  return Object.entries(object).filter(([key, val]) => !val.hidden)
}

const orderedFields = ref({})
onMounted(() => {
  if (props.fields.length > 0) {
    for (const field of props.fields) {
      orderedFields.value[field] = props.schema[field]
    }
  } else {
    orderedFields.value = props.schema
  }
})

watchEffect(() => {
  orderedFields.value = {}
  if (props.fields.length > 0) {
    for (const field of props.fields) {
      orderedFields.value[field] = props.schema[field]
    }
  } else {
    orderedFields.value = props.schema
  }
})

const emitEndDrag = (event) => {
  emit('endDrag', { oldIndex: event.oldIndex, newIndex: event.newIndex })
}

const emitStartDrag = () => {
  emit('startDrag', true)
}

const fieldHasError = ref({})

const shouldBeReadOnly = (key, index, keyName, options) => {
  if (options.exclude?.includes(keyName)) {
    return false
  }

  let readonly = false
  for (const option of Object.keys(options)) {
    if (option === 'exclude') {
      continue
    }

    if (writableModel.value[key][index][option] === options[option]) {
      readonly = true
      break
    }
  }
  return readonly
}
</script>

<template>
  <template v-for="(value, key, mainIndex) in orderedFields" :key="mainIndex">
    <template v-if="!value.hidden && value.type?.includes('custom')">
      <q-item v-if="props.showHeader" tag="div" top class="q-field__control q-field--with-bottom q-px-none">
        <q-item-section>
          <q-item-label class="text-h6 q-field__label vertical-padding bordered-header" :class="props.formErrors[key] ? 'text-negative' : ''">
            {{ $t(translationString + '.' + capitalize(key)) }}
            {{ props.formErrors[key] ? $t('Shared.Globals.Mandatory') : '' }}
            <q-icon v-if="props.formErrors[key]" name="error" size="sm"></q-icon>
          </q-item-label>
        </q-item-section>
      </q-item>
      <slot :name="'customField-' + key" :index="mainIndex" :row="writableModel[key]" :schema="value"></slot>
    </template>

    <form-field-generator
      v-if="value.type !== 'object' && value.type !== 'array-objects' && !value.listForm && (fields.length === 0 || fields.includes(key)) && !value.hidden"
      v-model="writableModel[key]"
      :dense="dense"
      data-lpignore="true"
      :options-config="optionsConfig?.field === key ? optionsConfig : undefined"
      :tabindex="0"
      :form-errors="props.formErrors[key]"
      :items-service="props.itemsService"
      :schema="value"
      :item-key="key"
      :parent-has-error="props.parentHasError"
      v-bind="$attrs"
      @field-error="receivedFieldError(key)"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="{ ...slotData, itemKey: key } || {}"></slot>
      </template>
    </form-field-generator>

    <q-card
      v-if="!value.hidden && value.type === 'array-objects' && nested"
      id="draggable-container"
      flat
      class="q-pa-none q-ma-none"
      :style="value.style"
      :class="{ [$attrs['field-class']]: !!$attrs['field-class'] }"
    >
      <q-card-section v-if="props.showHeader || props.formErrors[key]" class="q-pa-none">
        <q-item tag="div" top class="q-field__control q-field--with-bottom q-px-none">
          <q-item-section>
            <q-item-label
              class="text-h6 q-field__label vertical-padding"
              :class="{ 'text-negative': props.formErrors[key], 'bordered-header': props.showHeader }"
            >
              {{ $t($frontendType + '.' + capitalize(props.itemsService + '.' + capitalize(key))) }}
              {{ props.formErrors[key] ? $t('Shared.Globals.Mandatory') : '' }}
              <q-icon v-if="props.formErrors[key]" name="error" size="sm"></q-icon>
              <slot :name="'append-header-' + key"></slot>
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-card-section>
      <q-card-section class="q-pa-none">
        <q-markup-table flat class="q-pa-none q-ma-none sortable-overflow omni-form-array-table">
          <thead>
            <q-tr no-hover>
              <q-th v-if="value.draggable && writableModel[key]?.length > 1 && !value.readonly" class="no-padding" auto-width></q-th>
              <q-th
                v-for="(headerValue, headerName, headerIndex) in Object.fromEntries(objectMinusHiddenFields(value.value))"
                :key="headerIndex"
                class="text-left q-px-xs"
                style="padding-left: 4px; padding-right: 4px"
                :auto-width="headerValue.autoWidth ?? true"
                :style="headerValue.style"
              >
                <template v-if="!headerValue.enableCheckAll">
                  {{ $t(translationString + '.' + capitalize(headerValue.translateKey ?? headerName)) }}
                </template>
                <template v-else>
                  <q-checkbox v-model="checkAll[key]" :disable="readOnlyOn?.isCredit === true && writableModel[key].every((line) => line.isCredit === true)">
                    <q-tooltip>{{ $t(translationString + '.' + capitalize(headerName)) }}</q-tooltip>
                  </q-checkbox>
                </template>
              </q-th>
              <q-th
                v-if="
                  !value.readonly &&
                  !value.hideRemoveButton &&
                  (value.minItems ? writableModel[key].length > value.minItems : true) &&
                  !value.readonly &&
                  !$attrs.readonly
                "
              ></q-th>
            </q-tr>
          </thead>
          <template v-if="writableModel[key]?.length > 0">
            <draggable
              v-bind="dragOptions"
              v-model="writableModel[key]"
              tag="tbody"
              handle=".column-drag-handle"
              direction="vertical"
              :component-data="{
                type: 'transition-group',
                cursor: 'grabbing',
              }"
              :item-key="key"
              :disabled="value.readonly"
              @start="emitStartDrag"
              @end="emitEndDrag"
            >
              <template #item="{ element, index }">
                <q-tr>
                  <q-td
                    v-if="value.draggable && writableModel[key].length > 1 && !value.readonly"
                    class="ghost-border sized"
                    :colspan="objectMinusHiddenFields(value.value).length + 2"
                  ></q-td>
                  <q-td v-if="value.draggable && writableModel[key].length > 1 && !value.readonly" auto-width class="vertical-top no-padding">
                    <q-icon class="column-drag-handle" name="menu" size="xs"></q-icon>
                  </q-td>
                  <q-td
                    v-for="(keyValue, keyName, keyIndex) in Object.fromEntries(objectMinusHiddenFields(value.value))"
                    :key="keyIndex"
                    class="vertical-top array-objects"
                    :auto-width="keyValue.autoWidth ?? true"
                  >
                    <slot
                      v-if="keyValue?.type.includes('custom')"
                      :index="index"
                      :row="writableModel[key][index]"
                      :schema="keyValue"
                      :name="'custom-' + keyName"
                      v-bind="$attrs"
                      :readonlyfield="shouldBeReadOnly(key, index, keyName, props.readOnlyOn)"
                    ></slot>
                    <form-field-generator
                      v-if="!keyValue?.type.includes('custom')"
                      v-model="element[keyName]"
                      data-lpignore="true"
                      :options-config="props.optionsConfig?.field === keyName ? props.optionsConfig : undefined"
                      :tabindex="0"
                      :readonly="
                        (element['attrs'] && element['attrs'][keyName] && element['attrs'][keyName]['readonly']) ||
                        keyValue.readonly ||
                        shouldBeReadOnly(key, index, keyName, props.readOnlyOn)
                      "
                      :form-errors="
                        props.formErrors[key] && props.formErrors[key][index] && props.formErrors[key][index][keyName]
                          ? props.formErrors[key][index][keyName]
                          : []
                      "
                      :items-service="props.itemsService"
                      :schema="keyValue"
                      :item-key="keyName"
                      :parent-has-error="!!props.formErrors[key]"
                      :class="{
                        'no-padding': !(
                          (fieldHasError[key] && fieldHasError[key].index === index && fieldHasError[key].keyName === keyName) ||
                          (props.formErrors[key] && props.formErrors[key][index] && props.formErrors[key][index][keyName])
                        ),
                        [keyValue.class]: !!keyValue.class,
                      }"
                      :style="keyValue.style"
                      v-bind="$attrs"
                      @field-error="receivedFieldError(key, index, keyName)"
                      @field-no-error="receivedFieldNoError(key, index, keyName)"
                    />
                  </q-td>
                  <q-td
                    auto-width
                    class="vertical-top text-right no-padding"
                    v-if="
                      !value.readonly &&
                      !value.hideRemoveButton &&
                      (value.minItems ? writableModel[key].length > value.minItems : true) &&
                      !value.readonly &&
                      !$attrs.readonly
                    "
                  >
                    <slot :index="index" :row="writableModel[key][index]" :schema="value" name="additionalActionButtons"></slot>
                    <q-icon
                      class="q-pt-lg cursor-pointer"
                      name="sym_o_delete"
                      color="negative"
                      size="sm"
                      :aria-label="$t(translationString + '.Delete ' + capitalize(key) + ' item')"
                      @click="removeItemFromArray(key, index)"
                    >
                      <q-tooltip class="">
                        {{ $t(translationString + '.Delete ' + capitalize(key) + ' item') }}
                      </q-tooltip>
                    </q-icon>
                  </q-td>
                </q-tr>
              </template>
            </draggable>
          </template>
        </q-markup-table>
        <q-icon
          v-if="!value.hideAddButton && !value.readonly && !$attrs.readonly && (!value.maxLength || writableModel[key].length < value.maxLength)"
          class="no-padding cursor-pointer"
          name="add"
          size="sm"
          :aria-label="$t(translationString + '.Add ' + capitalize(key) + ' item')"
          @click="addObjectItemToArray(key)"
        >
          <q-tooltip>
            {{ $t(translationString + '.Add ' + capitalize(key) + ' item') }}
          </q-tooltip>
        </q-icon>
      </q-card-section>
    </q-card>

    <q-card
      v-if="!value.hidden && value.type === 'array-strings' && value.listForm"
      flat
      class="q-pa-none q-ma-none"
      :class="{ [$attrs['field-class']]: !!$attrs['field-class'] }"
    >
      <q-card-section v-if="props.showHeader" class="q-pa-none q-pb-20">
        <q-item tag="div" top class="q-field__control q-field--with-bottom q-px-none">
          <q-item-section>
            <q-item-label class="text-h6 q-field__label vertical-padding bordered-header" :class="props.formErrors[key] ? 'text-negative' : ''">
              {{ $t($frontendType + '.' + capitalize(props.itemsService + '.' + capitalize(key))) }}
              {{ props.formErrors[key] ? $t('Shared.Globals.Mandatory') : '' }}
              <q-icon v-if="props.formErrors[key]" name="error" size="sm"></q-icon>
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-card-section>
      <q-card-section class="q-pa-none">
        <template v-if="writableModel[key]?.length > 0">
          <div v-for="(nameserver, nsIndex) of writableModel[key]" :key="nsIndex" class="row q-pb-md">
            <div class="col q-pr-md">
              <form-field-generator
                v-model="writableModel[key][nsIndex]"
                data-lpignore="true"
                :options-config="props.optionsConfig ? props.optionsConfig : undefined"
                :tabindex="0"
                :form-errors="props.formErrors[key] && props.formErrors[key][nsIndex] && props.formErrors[key][nsIndex] ? props.formErrors[key][nsIndex] : []"
                :items-service="props.itemsService"
                :schema="value.fieldSchema"
                :required="nsIndex < value.minItems"
                :item-key="key"
                :label="$t(translationString + '.' + capitalize(key)) + ` ${nsIndex + 1}` + (nsIndex < value.minItems ? ' *' : '')"
                :parent-has-error="!!props.formErrors[key]"
                :class="{
                  'no-padding': !(
                    (fieldHasError[key] && fieldHasError[key].index === nsIndex) ||
                    (props.formErrors[key] && props.formErrors[key][nsIndex] && props.formErrors[key][nsIndex])
                  ),
                }"
                v-bind="$attrs"
                @field-error="receivedFieldError(key, nsIndex)"
                @field-no-error="receivedFieldNoError(key, nsIndex)"
              />
            </div>
            <div class="col-auto column items-end q-my-auto">
              <q-btn
                v-if="value.minItems ? writableModel[key].length > value.minItems : true"
                class="btn--no-hover action-buttons text-negative"
                flat
                icon="sym_o_delete"
                :aria-label="$t(translationString + '.Delete ' + capitalize(key) + ' item')"
                @click="removeItemFromArray(key, nsIndex)"
              >
                <q-tooltip class="">
                  {{ $t(translationString + '.Delete ' + capitalize(key) + ' item') }}
                </q-tooltip>
              </q-btn>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <q-btn
                v-if="writableModel[key].length <= value.maxLength - 1"
                flat
                class="q-px-xs q-ma-none q-mb-xs btn--no-hover"
                icon="add"
                :aria-label="$t(translationString + '.Add ' + capitalize(key) + ' item')"
                @click="addItemToArray(key)"
              >
                <q-tooltip>
                  {{ $t(translationString + '.Add ' + capitalize(key) + ' item') }}
                </q-tooltip>
              </q-btn>
            </div>
          </div>
        </template>
      </q-card-section>
    </q-card>

    <q-card v-if="!value.hidden && value.type === 'object' && nested" flat class="q-pa-none q-ma-none">
      <q-card-section v-if="props.showHeader" class="q-pa-none q-pb-20">
        <q-item tag="div" top class="q-field__control q-field--with-bottom q-px-none">
          <q-item-section>
            <q-item-label class="text-h6 q-field__label vertical-padding bordered-header" :class="props.formErrors[key] ? 'text-negative' : ''">
              {{ $t(translationString + '.' + capitalize(key)) }}
              {{ props.formErrors[key] ? $t('Shared.Globals.Mandatory') : '' }}
              <q-icon v-if="props.formErrors[key]" name="error" size="sm"></q-icon>
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-card-section>
      <q-card-section class="q-pa-none">
        <form-generator
          v-model="writableModel[key]"
          :options-config="props.optionsConfig"
          :current-tab-index="currentTabIndex + mainIndex"
          :form-errors="props.formErrors[key]"
          :items-service="props.itemsService"
          :schema="props.schema[key].value"
          :parent-has-error="!!props.formErrors[key]"
          v-bind="$attrs"
          @form-error="receivedFormError"
        >
        </form-generator>
      </q-card-section>
    </q-card>
  </template>
</template>

<style scoped></style>
